/**
 * AuthContext - Custom authentication context for WorkOS
 *
 * This replaces AuthKitProvider to give us full control over the auth flow
 * with our Cloudflare Workers backend.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

import {
  createIdentityPayload,
  storeIdentity,
  getStoredIdentity,
  clearStoredIdentity,
} from "../utils/sharedIdentity";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        console.log("[Auth] Checking session...");
        const response = await fetch("/api/auth/session", {
          credentials: "include",
        });
        const data = await response.json();

        if (data.user) {
          console.log("[Auth] Session found for user:", data.user.id);
          setUser(data.user);

          // Store shared identity for cross-app recognition
          const existingIdentity = getStoredIdentity();
          const marketplaceIdentity = createIdentityPayload({
            email: data.user.email,
            displayName: data.user.firstName
              ? `${data.user.firstName} ${data.user.lastName || ""}`.trim()
              : data.user.email?.split("@")[0],
            avatarUrl: data.user.profilePictureUrl || "",
            source: "marketplace",
            sourceId: data.user.id,
          });

          // Preserve wallet connection status if already linked
          if (existingIdentity?.wallet_connected) {
            marketplaceIdentity.wallet_connected = true;
            marketplaceIdentity.wallet_address_count =
              existingIdentity.wallet_address_count || 0;
            marketplaceIdentity.wallet_payment_method_count =
              existingIdentity.wallet_payment_method_count || 0;
          }

          storeIdentity(marketplaceIdentity);
        } else {
          console.log("[Auth] No session found");
          setUser(null);
        }
      } catch (err) {
        console.error("[Auth] Error checking session:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();
  }, []);

  // Sign in - redirect to WorkOS
  const signIn = useCallback(async (options = {}) => {
    try {
      // Fetch config to get client ID
      const configResponse = await fetch("/api/config");
      const config = await configResponse.json();

      if (!config.workosClientId) {
        throw new Error("WorkOS client ID not configured");
      }

      // Store return URL
      const returnTo = options.returnTo || window.location.pathname;
      localStorage.setItem("returnTo", returnTo);

      // Redirect to WorkOS hosted auth
      // The redirect_uri should point to our backend callback
      const redirectUri = `${window.location.origin}/api/auth/callback`;
      const state = encodeURIComponent(returnTo);

      const authUrl = new URL("https://api.workos.com/user_management/authorize");
      authUrl.searchParams.set("client_id", config.workosClientId);
      authUrl.searchParams.set("redirect_uri", redirectUri);
      authUrl.searchParams.set("response_type", "code");
      authUrl.searchParams.set("provider", "authkit");
      authUrl.searchParams.set("state", state);

      console.log("[Auth] Redirecting to WorkOS:", authUrl.toString());
      window.location.href = authUrl.toString();
    } catch (err) {
      console.error("[Auth] Sign in error:", err);
      setError(err.message);
    }
  }, []);

  // Sign out
  const signOut = useCallback(async () => {
    try {
      await fetch("/api/auth/signout", {
        method: "POST",
        credentials: "include",
      });
      setUser(null);
      clearStoredIdentity();
      console.log("[Auth] Signed out");
      window.location.href = "/";
    } catch (err) {
      console.error("[Auth] Sign out error:", err);
    }
  }, []);

  // Get access token from session
  const getAccessToken = useCallback(async () => {
    try {
      const response = await fetch("/api/auth/session", {
        credentials: "include",
      });
      const data = await response.json();
      return data.accessToken || null;
    } catch (err) {
      console.error("[Auth] Error getting access token:", err);
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        error,
        signIn,
        signOut,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
